<template>
  <v-app id="inspire">
    <div id="app">
      <router-view/>
      <notification-prompt v-if="false" />
      <chat-widget />

      <v-tour name="agentDrawerTour" :steps="agentDrawerTourSteps" :options="agentDrawerTourOptions" />
      <v-tour name="customerDrawerTour" :steps="customerDrawerTourSteps" :options="agentDrawerTourOptions" />

      <!-- PWA Install Prompt -->
      <v-snackbar
        v-model="showInstallPromptOnPage"
        :timeout="-1"
        bottom
        color="primary"
        class="mb-4 install-snackbar"
      >
       Pacis app — click to install.
        <template v-slot:action="{ attrs }">
          <v-btn
            text
            v-bind="attrs"
            @click="installApp"
            font-weight="bold"
          >
            Install
          </v-btn>
          <v-btn
            icon
            v-bind="attrs"
            @click="showInstallPrompt = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </template>
      </v-snackbar>
    </div>
  </v-app>
</template>

<script>

import NotificationPrompt from '@/components/shared/NotificationsPrompt';
import ChatWidget from '@/components/shared/ChatWidget';
import { mapGetters } from 'vuex';
import { ACCESS_TOKEN, NOTIFICATIONS, USER } from '@/utils/const';
import firebase from 'firebase/app';
import 'firebase/analytics';
import helpers from '@/utils/helpers';
import { bootstrapPortalCache, isAgentPortal, setupPortalCachePaymentListeners } from '@/utils/portalBootstrap';
import { version } from '../package.json';

const TOUR_GROUPS = {
  UNDERWRITING: 'Underwriting',
  CLAIMS: 'Claims',
  FINANCE: 'Finance',
  REPORTS: 'Reports',
};

const EMIT_AGENT_TOUR_OPEN = 'agent-tour-open-group';

export default {
  name: 'App',
  components: { NotificationPrompt, ChatWidget },
  data() {
    return {
      deferredPrompt: null,
      showInstallPrompt: false,
    };
  },
  computed: {
    ...mapGetters('auth', ['isAuthenticated']),
    isCustomerPortal() {
      return !isAgentPortal();
    },
    showInstallPromptOnPage: {
      get() {
        if (!this.showInstallPrompt) return false;
        const authRoutes = ['SignIn', 'SignUp', 'ForgotPassword', 'ConfirmOTP', 'ResetPassword', 'ResetSuccess'];
        return !authRoutes.includes(this.$route.name);
      },
      set(val) {
        this.showInstallPrompt = val;
      },
    },
    agentDrawerTourOptions() {
      return {
        useKeyboardNavigation: true,
        labels: {
          buttonSkip: 'Skip',
          buttonPrevious: 'Previous',
          buttonNext: 'Next',
          buttonStop: 'Finish',
        },
      };
    },
    agentDrawerTourSteps() {
      const hub = this.$eventsHub;
      return [
        {
          target: '[data-agent-tour="dashboard"]',
          header: { title: 'Dashboard' },
          content: 'Your main overview. View financial summary, active quotes, closed sales, and quick links to key actions.',
          params: { placement: 'right' },
        },
        {
          target: '[data-agent-tour="underwriting"]',
          header: { title: TOUR_GROUPS.UNDERWRITING },
          content: 'Manage Conversions, Renewals, and generate new quotes. Click to expand and access each option.',
          params: { placement: 'right' },
          before: () => {
            hub.$emit(EMIT_AGENT_TOUR_OPEN, TOUR_GROUPS.UNDERWRITING);
            return new Promise((resolve) => { this.$nextTick(resolve); });
          },
        },
        {
          target: '[data-agent-tour="claims"]',
          header: { title: TOUR_GROUPS.CLAIMS },
          content: 'View and manage client claims. Access claim details and payment history.',
          params: { placement: 'right' },
          before: () => {
            hub.$emit(EMIT_AGENT_TOUR_OPEN, TOUR_GROUPS.CLAIMS);
            return new Promise((resolve) => { this.$nextTick(resolve); });
          },
        },
        {
          target: '[data-agent-tour="finance"]',
          header: { title: TOUR_GROUPS.FINANCE },
          content: 'Track earnings and payments. View Commissions (paid and pending) and Transactions.',
          params: { placement: 'right' },
          before: () => {
            hub.$emit(EMIT_AGENT_TOUR_OPEN, TOUR_GROUPS.FINANCE);
            return new Promise((resolve) => { this.$nextTick(resolve); });
          },
        },
        {
          target: '[data-agent-tour="reports"]',
          header: { title: TOUR_GROUPS.REPORTS },
          content: 'Analytics and reports. View sales performance, product distribution, and export data.',
          params: { placement: 'right' },
        },
        {
          target: '[data-agent-tour="settings"]',
          header: { title: 'Settings' },
          content: 'Manage your agent profile and account settings.',
          params: { placement: 'right' },
        },
      ];
    },
    customerDrawerTourSteps() {
      return [
        {
          target: '[data-customer-tour="dashboard"]',
          header: { title: 'Dashboard' },
          content: 'Your home overview. See policy summaries, quick actions, and important updates at a glance.',
          params: { placement: 'right' },
        },
        {
          target: '[data-customer-tour="products"]',
          header: { title: 'Products' },
          content: 'Browse and buy insurance cover — Motor, Travel, Personal Accident, and more.',
          params: { placement: 'right' },
        },
        {
          target: '[data-customer-tour="my-policies"]',
          header: { title: 'My Policies' },
          content: 'View your active and past policies, certificates, and policy documents.',
          params: { placement: 'right' },
        },
        {
          target: '[data-customer-tour="activity"]',
          header: { title: 'Activity' },
          content: 'Track saved quotes and recent activity so you can continue where you left off.',
          params: { placement: 'right' },
        },
        {
          target: '[data-customer-tour="claims"]',
          header: { title: 'Claims' },
          content: 'Report a new claim or follow up on existing claims and their status.',
          params: { placement: 'right' },
        },
      ];
    },
  },
  metaInfo: {
    title: 'Pacis Insurance',
    titleTemplate: 'Customer Portal | %s',
    changed(metaInfo) {
      firebase.analytics().setCurrentScreen(metaInfo.title);
      firebase.analytics().logEvent('page_view');
      firebase.analytics().logEvent('screen_view', {
        app_name: helpers.isPWA() ? 'pwa' : 'web',
        screen_name: metaInfo.title,
        app_version: version,
      });
    },
  },
  beforeCreate() {
    if (this.$store.getters['auth/isAuthenticated'] && (this.$cookies.get(USER) === null || this.$cookies.get(ACCESS_TOKEN) === null)) {
      this.$store.dispatch('auth/signOut').then(() => {
        this.$store.commit('setClient', []);
        localStorage.removeItem(NOTIFICATIONS);
        this.$router.push({ name: 'SignIn' });
      });
    }
  },
  created() {
    window.addEventListener('offline', () => {
      this.$toast.error('Seems you\'re offline!', 'No Internet');
    });
    window.addEventListener('online', () => {
      this.$toast.success('You\'re back online', 'Online');
      if (window.location.pathname === '/no-internet') {
        this.$router.go(-1);
      }
    });

    // PWA Install Logic
    window.addEventListener('beforeinstallprompt', (e) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Stash the event so it can be triggered later.
      this.deferredPrompt = e;
      // Update UI notify the user they can install the PWA
      if (!helpers.isPWA()) {
        this.showInstallPrompt = true;
      }
    });

    window.addEventListener('appinstalled', () => {
      // Log install event to analytics
      firebase.analytics().logEvent('app_installed');
      this.deferredPrompt = null;
      this.showInstallPrompt = false;
    });
  },
  mounted() {
    setupPortalCachePaymentListeners(this.$store, this.$root);
    if (this.isAuthenticated) {
      bootstrapPortalCache(this.$store);
    }
  },
  methods: {
    async installApp() {
      if (!this.deferredPrompt) return;
      // Show the install prompt
      this.deferredPrompt.prompt();
      // Wait for the user to respond to the prompt
      const { outcome } = await this.deferredPrompt.userChoice;
      // eslint-disable-next-line no-console
      console.log(`User response to the install prompt: ${outcome}`);
      // We've used the prompt, and can't use it again, throw it away
      this.deferredPrompt = null;
      this.showInstallPrompt = false;
    },
  },
};
</script>
